import MP4Box, { type MP4ArrayBuffer, type MP4File, type MP4Info } from "mp4box";

interface MP4ArrayBufferValue {
    buffer: { fileStart: number };
    length: number;
}

const getNextChunk =
    (mp4File: MP4File, reader: ReadableStreamDefaultReader<MP4ArrayBuffer>, offset = 0) =>
    ({
        done,
        value,
    }: ReadableStreamReadResult<MP4ArrayBufferValue>):
        | Promise<ReadableStreamReadResult<MP4ArrayBufferValue> | undefined>
        | undefined => {
        if (done) {
            mp4File.flush();
            return void 0;
        }

        const { buffer, length } = value;

        buffer.fileStart = offset;
        offset += length;

        mp4File.appendBuffer(buffer);

        return reader.read().then(getNextChunk(mp4File, reader, offset));
    };

const onReady = (mp4File: MP4File): Promise<MP4Info> =>
    new Promise<MP4Info>((resolve, reject) => {
        mp4File.onReady = (info: MP4Info) => {
            resolve(info);
        };

        mp4File.onError = (e: string) => {
            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- mp4box uses string errors
            reject(e);
        };
    });

export const getFileMetadata = (file: File): Promise<MP4Info> => {
    /*
        This use of mp4box is strictly approved for this web client.
        Please see security review https://jira.sc-corp.net/browse/REV-10644 for more
        information and contact #appsec-guests if you are interested in using mp4box.
    */
    const mp4File = MP4Box.createFile();

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- @types/node overrides the correct `File` type from TypeScript
    const reader = (file.stream() as { getReader: () => ReadableStreamDefaultReader<MP4ArrayBuffer> }).getReader();

    reader
        .read()
        .then(getNextChunk(mp4File, reader))
        .catch(() => {
            // TODO - Error handling
        });

    return onReady(mp4File);
};
